import { Task } from '@griply/types';
import { todayFilterOptions } from '@griply/utils';
import {
  useTasksByFilterDocument,
  useTodaysCompletedHabits,
  useTodaysCompletedTasks,
  useTodaysTodos,
} from 'features/tasks';
import uniqBy from 'lodash/uniqBy';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';

import { getSidePanelFilterDocument } from '../utils/get-side-panel-filter-document';

export const useSidePanelTasks = (): Task[] => {
  const today = useToday();
  const user = useUser();

  const filterDocument = useMemo(
    () => getSidePanelFilterDocument(today, user?.settings ?? undefined),
    [today, user?.settings],
  );

  const filteredTasks = useTasksByFilterDocument(
    filterDocument,
    todayFilterOptions,
  );
  // Uncompleted due-today todos already come tag-filtered from the document
  // read; this read only contributes tasks completed before today (a
  // completed-today merge can't see them), so completion is required here.
  const dueTodayTodos = useTodaysTodos({ uncompletedOnly: false });
  const completedTasks = useTodaysCompletedTasks();
  const completedHabits = useTodaysCompletedHabits();

  return useMemo(() => {
    const dueTodayCompleted = dueTodayTodos.filter(
      (task) => !!task.completedAt && !task.deletedAt,
    );
    return uniqBy(
      [
        ...filteredTasks,
        ...dueTodayCompleted,
        ...completedTasks,
        ...completedHabits,
      ],
      'id',
    );
  }, [filteredTasks, dueTodayTodos, completedTasks, completedHabits]);
};
